/**
 * Envío LMX-T · recuadro con nombre en el canvas («1 · Flyers PDLP ×5»).
 * Se mueve desde el título, se agranda desde la esquina, el nombre se edita
 * con doble clic. Es sólo visual: los nodos de adentro no dependen de él.
 */
import React, { memo, useCallback, useRef, useState } from 'react';
import { useReactFlow } from 'reactflow';
import { X, Palette } from 'lucide-react';
import { useSectionsStore } from '../../store/sectionsStore';
import { SECTION_COLORS } from '../../lmx/trabajos';

const MIN_W = 160;
const MIN_H = 120;

const CanvasSection = memo(({ data }) => {
  const s = data?.section || {};
  const { getZoom } = useReactFlow();
  const update = useSectionsStore((st) => st.update);
  const patchLocal = useSectionsStore((st) => st.patchLocal);
  const save = useSectionsStore((st) => st.save);
  const remove = useSectionsStore((st) => st.remove);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(s.name || '');
  const dragRef = useRef(null);

  const commitName = useCallback(() => {
    setEditing(false);
    const v = name.trim() || 'Sección';
    if (v !== s.name) update(s.id, { name: v });
  }, [name, s.id, s.name, update]);

  const nextColor = useCallback((e) => {
    e.stopPropagation();
    const i = SECTION_COLORS.indexOf(s.color);
    update(s.id, { color: SECTION_COLORS[(i + 1) % SECTION_COLORS.length] });
  }, [s.color, s.id, update]);

  const startResize = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    const zoom = getZoom() || 1;
    dragRef.current = { x: e.clientX, y: e.clientY, w: s.w, h: s.h, zoom };
    const move = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      patchLocal(s.id, {
        w: Math.max(MIN_W, Math.round(d.w + (ev.clientX - d.x) / d.zoom)),
        h: Math.max(MIN_H, Math.round(d.h + (ev.clientY - d.y) / d.zoom)),
      });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      dragRef.current = null;
      const cur = useSectionsStore.getState().sections.find((x) => x.id === s.id);
      if (cur) save(s.id, { w: cur.w, h: cur.h });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, [getZoom, patchLocal, s.h, s.id, s.w, save]);

  return (
    <div className="cs-box" style={{ '--cs': s.color || '#4ea2ff' }} data-testid={`canvas-section-${s.id}`}>
      <div className="cs-head" title="Arrastrá para mover la sección · doble clic en el nombre para cambiarlo">
        <span className="cs-dot" aria-hidden />
        {editing ? (
          <input
            className="cs-name-input nodrag"
            value={name}
            autoFocus
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitName();
              if (e.key === 'Escape') { setName(s.name || ''); setEditing(false); }
              e.stopPropagation();
            }}
          />
        ) : (
          <span className="cs-name" onDoubleClick={(e) => { e.stopPropagation(); setName(s.name || ''); setEditing(true); }}>
            {s.name || 'Sección'}
          </span>
        )}
        <button type="button" className="cs-btn nodrag" onClick={nextColor} title="Cambiar color" aria-label="Cambiar color">
          <Palette size={12} />
        </button>
        <button type="button" className="cs-btn nodrag" onClick={(e) => { e.stopPropagation(); remove(s.id); }}
                title="Quitar el recuadro (los nodos no se tocan)" aria-label="Quitar sección">
          <X size={12} />
        </button>
      </div>
      <div className="cs-resize nodrag" onPointerDown={startResize} title="Arrastrá para agrandar" aria-hidden />
    </div>
  );
});

CanvasSection.displayName = 'CanvasSection';
export default CanvasSection;
